import React from 'react';
import { InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CustomTimePicker from '../../../../../../components/CustomTimePicker';
import { CustomTextField } from '../../../../../../components/CustomTextField';
import { CustomSelect } from '../../../../../../components/CustomSelect';
import {getModelSimpleSearch, userListToLabelEmailPhoneValue} from '../../../../../../Apis/functions';
import CustomAddChipSearch from '../../../../../../components/CustomAddChipSearch';
import {PersonChipBlack} from '../../../../../../components/buttons/IconButtons';
import {ATTENDANCE_SERVICE} from "../../../../../../reduxToolkit/EndPoints";
import {USER_TYPE_MODEL} from "../../../../../../enums/userTypeEnum";
import {RECORD_TIMEOUT_LIMITS, TOLERANCE_LIMITS, EXTENSION_DAYS_LIMITS, MAX_SHIFT_HOURS} from "./Model/Model";

const ShiftTimePicker = ({ readOnly, displayNone, emptyFields, item, handleChange, selectedList, setSelectedList, isValidRange = true }) => {
    const { t } = useTranslation();
    const { getAllDepartments } = useSelector((state) => state.documentPanelSlice);
    // extensionDays: overnight shifts require at least 1 (they cross midnight); diurnal shifts allow 0 //
    const extensionDaysMin = item?.requiresExtensionDay?.() ? 1 : EXTENSION_DAYS_LIMITS.MIN;
    const isValidText = isValidRange
        ? ''
        : t('policies_extension_days_invalid', { min: extensionDaysMin, max: EXTENSION_DAYS_LIMITS.MAX });

    // recordTimeout is required for overnight shifts //
    const requiresRecordTimeout = item?.requiresRecordTimeout ? item.requiresRecordTimeout() : false;

    // Shift duration limit + start/end must differ; never flag errors while read-only //
    const isShiftDurationValid = item?.isShiftDurationValid ? item.isShiftDurationValid() : true;
    const hasDistinctStartEnd = item?.hasDistinctStartEnd ? item.hasDistinctStartEnd() : true;
    const isStartEndValid = readOnly || (isShiftDurationValid && hasDistinctStartEnd);
    const startEndInvalidText = !hasDistinctStartEnd
        ? t('shift_start_end_equal')
        : t('shift_duration_max', { max: MAX_SHIFT_HOURS });

    // Tolerance (range): within its minute limits and not longer than the shift duration //
    const isRangeInLimits = item?.isRangeInLimits ? item.isRangeInLimits() : true;
    const isRangeWithinShift = item?.isRangeWithinShift ? item.isRangeWithinShift() : true;
    const isRangeValid = readOnly || (isRangeInLimits && isRangeWithinShift);
    const rangeInvalidText = !isRangeInLimits
        ? t('tolerance_range', { min: TOLERANCE_LIMITS.MIN, max: TOLERANCE_LIMITS.MAX })
        : t('tolerance_exceeds_shift');

    // Clock-in window: within its own limits //
    const isRecordTimeoutInLimits = item?.isRecordTimeoutInLimits ? item.isRecordTimeoutInLimits() : true;
    const isRecordTimeoutValid = readOnly || isRecordTimeoutInLimits;
    // Max is dynamic: half the idle gap outside the shift, derived from start/end //
    const maxRecordTimeout = item?.maxRecordTimeout ? item.maxRecordTimeout() : RECORD_TIMEOUT_LIMITS.MAX;
    const recordTimeoutInvalidText = t('record_timeout_range', { min: RECORD_TIMEOUT_LIMITS.MIN, max: maxRecordTimeout });

    return (
        <>
            {!displayNone && (
                <div className='col-12 col-md-12 mb-12'>
                    <CustomSelect
                        id={`slt_stp_department_${item?.id}`}
                        value={item?.departmentId}
                        name={"departmentId"}
                        readOnly={readOnly}
                        disabled={readOnly}
                        onChange={handleChange}
                        trans='department'
                        list={getAllDepartments}
                        keyName='id'
                        required={true}
                        emptyFields={emptyFields}
                        showNoSelectionOption={false}
                    />
                </div>
            )}
            <div className='col-12 col-md-12 mb-12'>
                <CustomAddChipSearch
                    id={`slt_stp_supervisor_${item?.id}`}
                    selectedList={selectedList}
                    setSelectedList={setSelectedList}
                    disabled={readOnly}
                    label='supervisor'
                    required={false}
                    reset={false}
                    hideButton={false}
                    keyId={'userId'}
                    showSubSection={true}
                    sanitizeDataList={userListToLabelEmailPhoneValue}
                    Icon={PersonChipBlack}
                    placeholder={t('search_employee_by_name_email_or_phone')}
                    api={item?.id ? ATTENDANCE_SERVICE.simpleSearchDepartmentSupervisor : undefined}
                    bodyApi={item?.id ? getModelSimpleSearch(null, { resourceId: item?.id}) : undefined}
                    userTypeIdsSearch={[USER_TYPE_MODEL.EMPLOYEE]}
                />
            </div>
            <div className='col-sm-12 col-md-6 '>
                <CustomTimePicker
                    id={`dtp_stp_start_shift_at_${item?.id}`}
                    title={t('starts_at')}
                    name={'startShiftAt'}
                    value={item?.startShiftAt}
                    handleChange={(value) => {
                        handleChange({ target: { value: value, name: 'startShiftAt' } });
                    }}
                    ampm={false}
                    disabled={readOnly}
                    required={true}
                    emptyFields={emptyFields}
                    isValid={isStartEndValid}
                    isValidText={startEndInvalidText}
                />
            </div>
            <div className='col-sm-12 col-md-6 '>
                <CustomTimePicker
                    id={`dtp_stp_end_shift_at_${item?.id}`}
                    title={t('ends_at')}
                    name={'endShiftAt'}
                    value={item?.endShiftAt}
                    handleChange={(value) => {
                        handleChange({ target: { value: value, name: 'endShiftAt' } });
                    }}
                    ampm={false}
                    disabled={readOnly}
                    required={true}
                    emptyFields={emptyFields}
                    isValid={isStartEndValid}
                    isValidText={startEndInvalidText}
                />
            </div>
            <div className='col-sm-12 col-md-6'>
                <CustomTextField
                    id={`txt_stp_tolerance_${item?.id}`}
                    value={item?.range === 0 ? null : item?.range}
                    trans='tolerance'
                    name={'range'}
                    required={true}
                    emptyFields={emptyFields}
                    disabled={readOnly}
                    isValid={isRangeValid}
                    isValidText={rangeInvalidText}
                    onChange={handleChange}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position='end'>
                                <span
                                    className={`ibl-font-w-700 ibl-h5 ${
                                        readOnly ? 'font-color-shades-300' : 'font-color-general-950'
                                    }`}
                                >
                                    {t('min')?.toUpperCase()}
                                </span>
                            </InputAdornment>
                        ),
                    }}
                    type='number'
                    length={4}
                    noUppercase={false}
                />
            </div>
            <div className='col-sm-12 col-md-6'>
                <CustomTextField
                    id={`txt_stp_extension_days_${item?.id}`}
                    value={item?.extensionDays}
                    trans='checkout_extension_days'
                    name='extensionDays'
                    type='number'
                    disabled={readOnly}
                    onChange={handleChange}
                    helperText={'set_checkout_extension_days'}
                    required={true}
                    emptyFields={emptyFields}
                    isValid={readOnly || isValidRange}
                    isValidText={isValidText}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position='end'>
                                <span
                                    className={`ibl-font-w-700 ibl-h5 ${
                                        readOnly ? 'font-color-shades-300' : 'font-color-general-950'
                                    }`}
                                >
                                    {t('days')?.toUpperCase()}
                                </span>
                            </InputAdornment>
                        ),
                    }}
                    length={10}
                />
            </div>
            <div className='col-sm-12 col-md-6'>
                <CustomTextField
                    id={`txt_stp_record_timeout_${item?.id}`}
                    value={item?.recordTimeout}
                    trans='record_timeout'
                    name={'recordTimeout'}
                    required={requiresRecordTimeout}
                    emptyFields={requiresRecordTimeout ? emptyFields : undefined}
                    disabled={readOnly}
                    isValid={isRecordTimeoutValid}
                    isValidText={recordTimeoutInvalidText}
                    onChange={handleChange}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position='end'>
                                <span
                                    className={`ibl-font-w-700 ibl-h5 ${
                                        readOnly ? 'font-color-shades-300' : 'font-color-general-950'
                                    }`}
                                >
                                    {t('min')?.toUpperCase()}
                                </span>
                            </InputAdornment>
                        ),
                    }}
                    type='number'
                    length={4}
                    noUppercase={false}
                    helperText={t(requiresRecordTimeout ? 'record_timeout_legend_required' : 'record_timeout_legend', { min: RECORD_TIMEOUT_LIMITS.MIN, max: maxRecordTimeout })}
                />
            </div>


        </>
    );
};

export default ShiftTimePicker;
